// RITA-1585 — Handbuch-Leiste neben dem Vertrag (Skizze 31.10.2026): Kopf „Handbuch“ mit ✕, darunter das Gerüst
// (Kapitelauswahl, Suche, Kapitelüberschrift), unten der Link „Ganzes Handbuch im neuen Tab öffnen“ zur Kachel beim
// gewählten Kapitel. Zwei Darstellungen (Platzregeln in model/handbuchplatz.ts):
// – Spalte: 380 px breit rechts neben dem Inhalt, klebt ab der Titelzeile (sticky, `top` = Unterkante des App-Kopfs
//   wie die Titelzeile) und scrollt innen.
// – Schublade: fest über dem rechten Rand, vom App-Kopf bis zur Heatmap-Leiste bzw. zum unteren Rand; die
//   Vertragsseite wird nie verkleinert. Ebene unter dem App-Kopf (Konvention im Editor: alles unter dem Kopf), über
//   Titelzeile (11), Werkzeugen (10) und Platzhalterleiste (9).
// Im Druck ist die Leiste ausgeblendet (der Druck zeigt ausschließlich den Vertrag).
import React from 'react';
import { Box, IconButton, Link, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { HEATMAP_LEISTE_PLATZ } from '../../usage-tracking/heatmap/leistenHoehe';
import { Handbuchdarstellung, HANDBUCH_BREITE_PX } from '../model/handbuchplatz';
import { handbuchAdresse } from '../model/handbuchAdresse';
import { Kapitelwahl } from '../model/handbuecher';
import HandbuchGeruest from './HandbuchGeruest';

export const HANDBUCH_LEISTE_TITEL = 'Handbuch';
export const HANDBUCH_SCHLIESSEN = 'Handbuch schließen';
export const HANDBUCH_NEUER_TAB = 'Ganzes Handbuch im neuen Tab öffnen';
/** Abstand der Leiste zum unteren Fensterrand (Spalte) bzw. zur Heatmap-Leiste. */
const UNTEN_LUFT_PX = 16;
/** Schublade: Platz für das feste Einstellungs-Zahnrad unten rechts (settings/drawer/ToggleButton: 24 px Rand, 52 px). */
const SCHUBLADE_UNTEN_PX = 88;

interface Props {
  darstellung: Handbuchdarstellung;
  /** Unterkante des App-Kopfs (gemessen, wie die Titelzeile); die Leiste beginnt darunter. */
  obenAbstandPx: number;
  auswahl: Kapitelwahl;
  onAuswahl: (wahl: Kapitelwahl) => void;
  onSchliessen: () => void;
}

export default function HandbuchSeitenleiste({ darstellung, obenAbstandPx, auswahl, onAuswahl, onSchliessen }: Props) {
  const hoehe = `calc(100vh - ${obenAbstandPx + UNTEN_LUFT_PX}px - ${HEATMAP_LEISTE_PLATZ})`;
  return (
    <Box
      component="aside"
      aria-label={HANDBUCH_LEISTE_TITEL}
      data-testid="handbuch-leiste"
      data-darstellung={darstellung}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
        '@media print': { display: 'none' },
        ...(darstellung === 'spalte'
          ? {
              flex: `0 0 ${HANDBUCH_BREITE_PX}px`,
              width: HANDBUCH_BREITE_PX,
              alignSelf: 'flex-start',
              position: 'sticky',
              top: obenAbstandPx,
              // So hoch wie der Inhalt, höchstens bis zum unteren Rand: Der Link unten bleibt ohne Scrollen sichtbar;
              // mit Text (RITA-901) scrollt die Leiste innen.
              maxHeight: hoehe,
            }
          : {
              position: 'fixed',
              top: obenAbstandPx,
              right: 0,
              width: `min(${HANDBUCH_BREITE_PX}px, calc(100vw - 16px))`,
              // Die Schublade lässt unten rechts das Einstellungs-Zahnrad frei (fest, 24 px Rand + 52 px Kasten).
              height: `calc(100vh - ${obenAbstandPx + SCHUBLADE_UNTEN_PX}px - ${HEATMAP_LEISTE_PLATZ})`,
              borderRadius: '8px 0 0 8px',
              boxShadow: 8,
              zIndex: (theme) => theme.zIndex.appBar - 2,
            }),
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <MenuBookIcon fontSize="small" color="primary" aria-hidden="true" />
        <Typography component="h2" variant="subtitle1" sx={{ fontWeight: 700, flex: '1 1 auto' }}>
          {HANDBUCH_LEISTE_TITEL}
        </Typography>
        <IconButton
          size="small"
          aria-label={HANDBUCH_SCHLIESSEN}
          onClick={onSchliessen}
          data-testid="handbuch-schliessen"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain', p: 1.5 }}>
        <HandbuchGeruest auswahl={auswahl} onAuswahl={onAuswahl} ueberschrift="h3" />
      </Box>
      <Box sx={{ px: 1.5, py: 1, borderTop: 1, borderColor: 'divider' }}>
        <Link
          href={handbuchAdresse(auswahl)}
          target="_blank"
          rel="noopener"
          underline="hover"
          variant="body2"
          data-testid="handbuch-neuer-tab"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
        >
          <OpenInNewIcon sx={{ fontSize: 16 }} aria-hidden="true" />
          {HANDBUCH_NEUER_TAB}
        </Link>
      </Box>
    </Box>
  );
}
